import React from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    CircularProgress,
    Rating,
} from "@mui/material";
import Carousel from "react-material-ui-carousel";
import { useParams } from "react-router-dom";
import { IMAGE_URL } from "../../store/services/constants";
import { useFetchBookingDetailsQuery } from "../../store/services/bookingService";

const BookingDetails = () => {
    const { id } = useParams(); // Fetch booking ID from URL
    const { data, isLoading, error } = useFetchBookingDetailsQuery(id);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: "center", padding: 4 }}>
                <Typography variant="h6" color="error">
                    Failed to load booking details. Please try again later.
                </Typography>
            </Box>
        );
    }

    const {
        user,
        ad,
        payment_transaction,
        start_date,
        end_date,
        pickup_location,
        status,
        booking_amount,
        created_at,
        rating,
    } = data.data;

    return (
        <Box sx={{ padding: "24px", backgroundColor: "#F5F5F5" }}>
            {/* Title */}
            <Typography variant="h4" sx={{ fontFamily: "PSBo", color: "rgba(247, 181, 0)", mb: 4 }}>
                Booking Details
            </Typography>

            <Grid container spacing={4}>
                {/* Carousel Section */}
                <Grid item xs={12} md={5}>
                    <Box
                        sx={{
                            flex: "1",
                            maxWidth: "100%",
                        }}
                    >
                        <Carousel animation="slide" autoPlay navButtonsAlwaysVisible indicators>
                            {ad.images?.map((image) => (
                                <Box key={image.id}>
                                    <img
                                        src={`${IMAGE_URL}${image.image}`}
                                        alt="Ad Image"
                                        style={{
                                            width: "100%",
                                            height: "300px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </Box>
                            ))}
                        </Carousel>
                    </Box>
                </Grid>

                {/* Right Section */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ padding: "16px", borderRadius: "12px" }}>
                        <Typography variant="h6" sx={{ fontFamily: "PSBo", fontSize: 16, mb: 2 }}>
                            Booking Info
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Status:</strong> {status}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Start Date:</strong> {new Date(start_date).toLocaleDateString()}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>End Date:</strong> {new Date(end_date).toLocaleDateString()}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Pickup Location:</strong> {pickup_location}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Booking Amount:</strong> ${booking_amount}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Created At:</strong> {new Date(created_at).toLocaleDateString()}
                        </Typography>

                        <Divider sx={{ marginY: "16px" }} />

                        {status === "completed" && rating ? (
                            <Box>
                                <Typography variant="h6" sx={{ fontFamily: "PSBo", mb: 2, fontSize: 16 }}>
                                    User Rating
                                </Typography>
                                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                                    <img
                                        src={user.image ? `${IMAGE_URL}${user.image}` : "/default-user.png"}
                                        alt="User"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                            marginRight: "10px",
                                        }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontFamily: "PRe", fontSize: "14px", mb: 0.5 }}>
                                            {user.username}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "PRe", fontSize: "12px", color: "gray" }}>
                                            {rating.rating_text} 
                                        </Typography>
                                    </Box>
                                </Box>
                                <Rating value={parseFloat(rating.ratings)} readOnly precision={0.5} />
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="h6" sx={{ fontFamily: "PSBo", mb: 2, fontSize: 16 }}>
                                    User Info
                                </Typography>
                                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                                    <img
                                        src={user.image ? `${IMAGE_URL}${user.image}` : "/default-user.png"}
                                        alt="User"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                            marginRight: "10px",
                                        }}
                                    />
                                    <Typography sx={{ fontFamily: "PRe", fontSize: "14px" }}>
                                        {user.username}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily: "PRe", fontSize: "14px" }}>
                                    <strong>User ID:</strong> {user.id}
                                </Typography>
                            </Box>
                        )}

                    </Paper>
                </Grid>
            </Grid>

            <Paper sx={{ padding: "16px", marginTop: "24px", borderRadius: "12px" }}>
                <Grid container spacing={2}>
                    {/* Ad Details */}
                    <Grid item xs={12} md={5}>
                        <Typography variant="h6" sx={{ fontFamily: "PSBo", mb: 2, fontSize: 16 }}>
                            Ad Details
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Name:</strong> {ad.name}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Description:</strong> {ad.description}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Price (Hourly):</strong> OMR {ad.price_hourly}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Features:</strong> {ad.features}
                        </Typography>
                    </Grid>

                    {/* Payment Details */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontFamily: "PSBo", mb: 2, fontSize: 16 }}>
                            Payment Info
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Booking Amount:</strong> OMR {payment_transaction?.booking_amount}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Admin Commission:</strong> OMR {payment_transaction?.admin_commission}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>User Amount:</strong> OMR {payment_transaction?.user_amount}
                        </Typography>
                        <Typography sx={{ fontFamily: "PRe", fontSize: 14, mb: 1 }}>
                            <strong>Transaction Date:</strong>{" "}
                            {new Date(payment_transaction?.transaction_date).toLocaleString()}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default BookingDetails;
